import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { AdminHeader, AdminBody } from "./components/AdminHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Trash2, UserPlus } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const UsersAdmin = () => {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const { data: users = [], isLoading } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/admin/users")).data });
  const adminCount = users.filter((u) => u.role === "admin").length;

  const setRole = useMutation({
    mutationFn: async ({ id, role }) => api.put(`/admin/users/${id}/role`, { role }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Rôle mis à jour"); },
  });

  const removeUser = useMutation({
    mutationFn: async (id) => api.delete(`/admin/users/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Utilisateur supprimé"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Suppression impossible"),
  });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "editor" });
  const invite = useMutation({
    mutationFn: async (body) => api.post("/admin/users", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Utilisateur invité"); setOpen(false); setForm({ name: "", email: "", role: "editor" }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Invitation impossible"),
  });

  return (
    <>
      <AdminHeader title="Utilisateurs" subtitle="Gérez les accès Admin et Éditeur" />
      <AdminBody>
        <div className="max-w-3xl mb-4 flex justify-end">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-f2b-black text-white hover:bg-f2b-black/90" data-testid="invite-user-open"><UserPlus size={16} className="mr-1.5" /> Inviter un utilisateur</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Inviter un utilisateur</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-f2b-text mb-1.5">Nom</label>
                  <Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="Nom complet" data-testid="invite-name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-f2b-text mb-1.5">E-mail</label>
                  <Input type="email" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} placeholder="prenom@exemple.com" data-testid="invite-email" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-f2b-text mb-1.5">Rôle</label>
                  <Select value={form.role} onValueChange={(role) => setForm((s) => ({ ...s, role }))}>
                    <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrateur</SelectItem>
                      <SelectItem value="editor">Éditeur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-f2b-gray">La connexion se fait via Google. L'utilisateur accèdera au back-office avec le rôle choisi dès sa première connexion avec cette adresse e-mail. Aucun mot de passe n'est créé.</p>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setOpen(false)} data-testid="invite-cancel">Annuler</Button>
                  <Button className="bg-f2b-black text-white hover:bg-f2b-black/90" disabled={invite.isPending || !form.email} onClick={() => invite.mutate(form)} data-testid="invite-submit">Inviter</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div className="bg-white border border-f2b-filet overflow-hidden max-w-3xl">
          <table className="w-full text-sm">
            <thead className="bg-f2b-light border-b border-f2b-filet"><tr>
              {["Nom", "E-mail", "Rôle", ""].map((h, i) => <th key={i} className="text-left px-4 py-3 font-medium text-f2b-gray">{h}</th>)}
            </tr></thead>
            <tbody>
              {isLoading ? <tr><td colSpan={4} className="px-4 py-10 text-center text-f2b-gray">Chargement…</td></tr>
                : users.map((u) => {
                  const isSelf = me?.user_id === u.user_id;
                  const isLastAdmin = u.role === "admin" && adminCount <= 1;
                  const canDelete = !isSelf && !isLastAdmin;
                  return (
                    <tr key={u.user_id} className="border-b border-f2b-filet last:border-0">
                      <td className="px-4 py-3 text-f2b-text font-medium">{u.name}</td>
                      <td className="px-4 py-3 text-f2b-gray">{u.email}</td>
                      <td className="px-4 py-3">
                        <Select value={u.role} onValueChange={(role) => setRole.mutate({ id: u.user_id, role })}>
                          <SelectTrigger className="h-8 w-40" data-testid={`user-role-${u.user_id}`}><SelectValue><Badge variant="secondary">{u.role === "admin" ? "Administrateur" : "Éditeur"}</Badge></SelectValue></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Administrateur</SelectItem>
                            <SelectItem value="editor">Éditeur</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canDelete ? (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button className="text-f2b-gray hover:text-red-600 transition-colors p-1.5" title="Supprimer" data-testid={`user-delete-${u.user_id}`}>
                                <Trash2 size={16} />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Le compte <strong>{u.name}</strong> ({u.email}) sera supprimé définitivement et ne pourra plus se connecter. Cette action est irréversible.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel data-testid="user-delete-cancel">Annuler</AlertDialogCancel>
                                <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => removeUser.mutate(u.user_id)} data-testid="user-delete-confirm">Supprimer</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        ) : (
                          <span className="text-f2b-gray/50 text-xs" title={isSelf ? "Votre propre compte" : "Dernier administrateur"}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        <p className="text-f2b-gray text-sm mt-4 max-w-3xl">
          <strong>Administrateur</strong> : tous les droits, y compris réglages, SEO et gestion des utilisateurs.
          <strong className="ml-2">Éditeur</strong> : gestion des contenus (éditions, programme, intervenants, articles, partenaires, inscriptions, médias) sans les réglages sensibles.
          <span className="block mt-2">La suppression est bloquée pour votre propre compte et pour le dernier administrateur.</span>
        </p>
      </AdminBody>
    </>
  );
};

export default UsersAdmin;
